import { motion } from 'framer-motion';
import { Mail, ExternalLink, Shield, HelpCircle, Clock } from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' }
  }
};

export const StaffPortalPage = () => {
  const handleEmailLogin = () => {
    window.open('https://mail.zoho.com', '_blank');
  };

  return (
    <div data-testid="staff-portal-page" className="bg-gradient-to-br from-slate-50 to-teal-50 min-h-screen">
      {/* Hero Section */}
      <section className="section-padding pt-32">
        <div className="container-main">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-2xl mx-auto text-center"
          >
            <motion.div variants={itemVariants} className="mb-8">
              <div className="w-20 h-20 bg-teal-600 mx-auto flex items-center justify-center mb-6">
                <Mail size={40} className="text-white" />
              </div>
              <span className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-4 block">
                Staff Portal
              </span>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                HSL Staff Email
              </h1>
              <p className="text-slate-600 text-lg">
                Access your HSL email account securely through our Zoho Mail portal.
              </p>
            </motion.div>

            {/* Login Button */}
            <motion.div variants={itemVariants} className="mb-12">
              <button
                onClick={handleEmailLogin}
                data-testid="email-login-btn"
                className="inline-flex items-center gap-3 bg-teal-600 text-white px-8 py-4 text-lg font-medium hover:bg-teal-700 transition-all hover:shadow-lg group"
              >
                <Mail size={24} />
                Access Your Email
                <ExternalLink size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="text-slate-500 text-sm mt-4">
                Opens Zoho Mail in a new tab
              </p>
            </motion.div>

            {/* Quick Links */}
            <motion.div 
              variants={itemVariants}
              className="grid md:grid-cols-3 gap-4 mb-12"
            >
              <a
                href="https://mail.zoho.com"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white border border-slate-200 p-4 hover:border-teal-500 hover:shadow-md transition-all group"
              >
                <Mail size={24} className="text-teal-600 mb-2 mx-auto" />
                <h3 className="font-medium text-slate-900">Webmail</h3>
                <p className="text-slate-500 text-sm">mail.zoho.com</p>
              </a>
              
              <a
                href="https://calendar.zoho.com"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white border border-slate-200 p-4 hover:border-teal-500 hover:shadow-md transition-all group"
              >
                <Clock size={24} className="text-teal-600 mb-2 mx-auto" />
                <h3 className="font-medium text-slate-900">Calendar</h3>
                <p className="text-slate-500 text-sm">calendar.zoho.com</p>
              </a>
              
              <a
                href="https://www.zoho.com/mail/help/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white border border-slate-200 p-4 hover:border-teal-500 hover:shadow-md transition-all group"
              >
                <HelpCircle size={24} className="text-teal-600 mb-2 mx-auto" />
                <h3 className="font-medium text-slate-900">Help & Support</h3>
                <p className="text-slate-500 text-sm">Get assistance</p>
              </a>
            </motion.div>
          </motion.div>

          {/* Info Cards */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="max-w-4xl mx-auto"
          >
            <div className="grid md:grid-cols-2 gap-6">
              {/* Security Notice */}
              <div className="bg-white border border-slate-200 p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <Shield size={20} className="text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-slate-900 mb-2">Security Notice</h3>
                    <ul className="text-slate-600 text-sm space-y-2">
                      <li>• Never share your password with anyone</li>
                      <li>• Always log out when using shared computers</li>
                      <li>• Report suspicious emails to IT immediately</li>
                      <li>• Enable two-factor authentication for added security</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Email Setup Help */}
              <div className="bg-white border border-slate-200 p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <HelpCircle size={20} className="text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-slate-900 mb-2">Need Help?</h3>
                    <ul className="text-slate-600 text-sm space-y-2">
                      <li>• <strong>Forgot password?</strong> Contact your administrator</li>
                      <li>• <strong>Mobile setup:</strong> Download Zoho Mail app</li>
                      <li>• <strong>Desktop client:</strong> Use IMAP/SMTP settings</li>
                      <li>• <strong>IT Support:</strong> info@hslhealth.pro</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Apps */}
            <div className="mt-6 bg-slate-900 text-white p-6">
              <h3 className="font-heading text-lg mb-4 text-center">Download Zoho Mail App</h3>
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="https://apps.apple.com/app/zoho-mail/id909262651"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-slate-900 px-6 py-2 font-medium hover:bg-slate-100 transition-colors"
                >
                  iOS App Store
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.zoho.mail"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-slate-900 px-6 py-2 font-medium hover:bg-slate-100 transition-colors"
                >
                  Google Play
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
